import { useMemo } from "react";

import { validateVirtualCategories } from "../../../common/helpers/virtualCategory";
import { useCatalogueContext } from "../../../contexts/CatalogueContext/CatalogueContext";
import { useConfigContext } from "../../../contexts/ConfigContext/ConfigContext";
import { IVirtualCategory } from "../../../contexts/ConfigContext/types";
import { ICategoryList } from "../types";

const VIRTUAL_CATEGORY_ID_PREFIX = "vc:";

interface ICategoryWithSort extends ICategoryList {
	displayOrder: number;
}

export const useCatalogueData = () => {
	const {
		state: { products, isLoading },
	} = useCatalogueContext((state) => state);
	const { state: config } = useConfigContext((state) => state.config);
	const { state: location } = useConfigContext((state) => state.location);

	const hiddenAlacarteIDs = config?.hidden_alacarte_bmi;
	const showVirtualCategory = config?.show_virtual_category;
	const rawVirtualCategories = config?.virtual_category;
	const brandUuid = location?.id ?? "";

	const filteredProducts = useMemo(() => {
		if (!products) {
			return [];
		}
		if (!hiddenAlacarteIDs || !Array.isArray(hiddenAlacarteIDs)) {
			return products;
		}

		return products.filter((product) => !hiddenAlacarteIDs.includes(product.brand_menu_item_id));
	}, [products, hiddenAlacarteIDs]);

	const validVirtualCategories = useMemo<IVirtualCategory[]>(() => {
		if (showVirtualCategory !== true) {
			return [];
		}

		return validateVirtualCategories(rawVirtualCategories, brandUuid);
	}, [showVirtualCategory, rawVirtualCategories, brandUuid]);

	const categories = useMemo<ICategoryList[]>(() => {
		if (!filteredProducts) {
			return [];
		}

		const realCategoriesMap = filteredProducts?.reduce<Map<string, ICategoryWithSort>>(
			(acc, product) => {
				const categoryId = product.food_supplier_category?.brand_category_id?.toString();

				if (categoryId && !acc.has(categoryId)) {
					const { description, description_cn } = product.food_supplier_category;

					acc.set(categoryId, {
						id: categoryId,
						name: product.food_supplier_category.title ?? "",
						name_cn: product.food_supplier_category.title_cn ?? "",
						kind: "real",
						displayOrder: product.food_supplier_category.display_order ?? 0,
						// Only carry descriptions when present so the category shape
						// stays minimal for categories without one (virtual categories
						// and real categories with no configured description).
						...(description ? { description } : {}),
						...(description_cn ? { description_cn } : {}),
					});
				}

				return acc;
			},
			new Map()
		);

		const realEntries = Array.from(realCategoriesMap.values());

		// Virtual categories that match zero items in the filtered set (e.g. when
		// hidden_alacarte_bmi removed every tagged item) are dropped — consistent with
		// the real-category empty-section behaviour in ProductList.tsx.
		const virtualEntries: ICategoryWithSort[] = validVirtualCategories
			.filter((vc) => filteredProducts.some((product) => product.tags?.includes(vc.tag_key)))
			.map((vc) => ({
				id: `${VIRTUAL_CATEGORY_ID_PREFIX}${vc.tag_key}`,
				name: vc.title,
				name_cn: vc.title_cn,
				kind: "virtual",
				displayOrder: vc.display_order,
			}));

		// Stable ascending sort by display_order. Real + virtual merged into a single list.
		// Backend assigns negative / decimal display_order values so collisions should not
		// happen in practice; when they do, source-order tie-break (stable sort) keeps
		// behaviour deterministic.
		return [...realEntries, ...virtualEntries]
			.sort((a, b) => a.displayOrder - b.displayOrder)
			.map(({ displayOrder, ...category }) => category);
	}, [filteredProducts, validVirtualCategories]);

	const sortedProducts = useMemo(() => {
		if (!filteredProducts) {
			return filteredProducts;
		}

		return [...filteredProducts].sort((a, b) => a.display_order - b.display_order);
	}, [filteredProducts]);

	return { categories, products: sortedProducts, isLoading };
};
